import React from "react";
import { api, TwoDaysResp } from "../api";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

/** Format HH:MM in New York time (handles EST/EDT automatically). */
function labelHM_NY(iso: string) {
  const d = new Date(iso);
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return fmt.format(d).replace(/\u202f|\u00a0/g, "");
}

/** Read a CSS var with a fallback. */
function cssVar(name: string, fallback: string) {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

/** 12.3k / 4.1M formatting for volume ticks/tooltip. */
function fmtK(n: number | null | undefined) {
  if (n == null || Number.isNaN(n)) return "—";
  if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (Math.abs(n) >= 1_000) return (n / 1_000).toFixed(1) + "k";
  return String(n);
}

export default function MinuteOverlayChart({ symbol }: { symbol: string }) {
  const [data, setData] = React.useState<TwoDaysResp | null>(null);
  const [err, setErr] = React.useState<string | null>(null);

  // toggles (all on by default; we can still auto-hide based on session below)
  const [showPrev, setShowPrev] = React.useState(true);
  const [showCurr, setShowCurr] = React.useState(true);
  const [showVol,  setShowVol ] = React.useState(true);

  // fullscreen
  const chartWrapRef = React.useRef<HTMLDivElement>(null);
  const [isFull, setIsFull] = React.useState(false);

  const toggleFullscreen = React.useCallback(() => {
    if (!document.fullscreenElement) {
      chartWrapRef.current?.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  }, []);

  React.useEffect(() => {
    const onFsChange = () => setIsFull(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  const load = React.useCallback(async () => {
    try {
      setData(await api.minuteTwoDays(symbol));
      setErr(null);
    } catch (e: any) {
      setErr(String(e));
    }
  }, [symbol]);

  React.useEffect(() => {
    load();
    const t = setInterval(load, 60_000); // ~real-time refresh
    return () => clearInterval(t);
  }, [load]);

  if (err) return <div className="card bad">{err}</div>;
  if (!data) return <div className="card">Loading minute data…</div>;

  const prev = data.previous.bars;
  const curr = data.current.bars;

  // If neither day has RTH bars, bail out gracefully.
  if (curr.length === 0 && prev.length === 0) {
    return <div className="card">No RTH minute data available.</div>;
  }

  // --- Choose which session drives the X-axis ---
  // usingCurr = true  → live market (or already has some of today’s bars)
  // usingCurr = false → closed/pre-open/holiday → show yesterday
  const usingCurr = curr.length > 0;

  let labels: string[];
  let currClose: (number | null)[];
  let currVol: (number | null)[];
  let prevClose: (number | null)[];

  if (usingCurr) {
    // Live: base labels on TODAY; align previous to those HH:MM slots
    labels    = curr.map((b) => labelHM_NY(b.ts));
    currClose = curr.map((b) => b.close);
    currVol   = curr.map((b) => b.volume ?? null);

    const prevMap = new Map(prev.map((b) => [labelHM_NY(b.ts), b.close]));
    prevClose = labels.map((l) => (prevMap.has(l) ? (prevMap.get(l) as number) : null));
  } else {
    // Closed: base labels on YESTERDAY and only show the previous session
    labels    = prev.map((b) => labelHM_NY(b.ts));
    prevClose = prev.map((b) => b.close);
    currClose = labels.map(() => null);
    currVol   = labels.map(() => null);
  }

  // Theme colors from CSS variables
  const colorPrev = cssVar("--chart-prev", "#64b5f6");
  const colorCurr = cssVar("--chart-curr", "#4dd0a6");
  const colorVol  = cssVar("--chart-vol", "rgba(164, 128, 255, 0.85)");
  const axisColor = cssVar("--chart-axis", "#a8bdcf");
  const gridColor = cssVar("--chart-grid", "#243141");
  const tooltipBg = cssVar("--tooltip-bg", "#0f141b");
  const text      = cssVar("--text", "#e7edf3");
  const border    = cssVar("--border", "#1f2a36");

  return (
    <div className="card">
      <div className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <h2 style={{ margin: 0 }}>
          {symbol} — Minute Overlay {usingCurr ? <small style={{opacity:.7}}>• live</small> : <small style={{opacity:.7}}>• previous session</small>}
        </h2>
        <button onClick={load}>refresh</button>
        {/* Toggles */}
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={showPrev} onChange={e => setShowPrev(e.target.checked)} />
          Prev
        </label>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={showCurr} onChange={e => setShowCurr(e.target.checked)} />
          Curr
        </label>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={showVol} onChange={e => setShowVol(e.target.checked)} />
          Volume
        </label>
        {/* Fullscreen */}
        <button
          title={isFull ? "Exit fullscreen (Esc)" : "Fullscreen"}
          onClick={toggleFullscreen}
          aria-pressed={isFull}
          style={{ marginLeft: "auto" }}
        >
          ⛶
        </button>
      </div>

      {/* Wrap just the chart area so only it goes fullscreen */}
      <div
        ref={chartWrapRef}
        className="chart"
        style={isFull ? { height: "80vh" } : undefined}
      >
        <Line
          data={{
            labels,
            datasets: [
              {
                label: `Prev (${data.previous.day})`,
                data: prevClose,
                borderColor: colorPrev,
                backgroundColor: colorPrev,
                borderWidth: 2,
                borderDash: [6, 4],
                pointRadius: 0,
                spanGaps: true,
                tension: 0.28,
                yAxisID: "y",
                hidden: !showPrev,
              },
              {
                label: `Curr (${data.current.day})`,
                data: currClose,
                borderColor: colorCurr,
                backgroundColor: colorCurr,
                borderWidth: 3,
                pointRadius: 0,
                spanGaps: true,
                tension: 0.28,
                yAxisID: "y",
                hidden: !showCurr || !usingCurr, // auto-hide when showing yesterday
              },
              // Volume on right axis
              {
                label: `Volume (${usingCurr ? data.current.day : data.previous.day})`,
                data: currVol,
                borderColor: colorVol,
                backgroundColor: colorVol,
                borderWidth: 1.4,
                pointRadius: 0,
                spanGaps: true,
                tension: 0.15,
                yAxisID: "yVol",
                hidden: !showVol || !usingCurr, // only show live volume
              },
            ],
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            normalized: true,
            interaction: { mode: "index", intersect: false },
            plugins: {
              legend: { display: true, position: "bottom", labels: { color: text } },
              tooltip: {
                backgroundColor: tooltipBg,
                borderColor: border,
                borderWidth: 1,
                titleColor: text,
                bodyColor: text,
                callbacks: {
                  title: (items) => (items[0] ? `NY ${items[0].label}` : ""),
                  label: (ctx) => {
                    const lbl = ctx.dataset.label || "";
                    const y = typeof ctx.parsed.y === "number" ? ctx.parsed.y : null;
                    if ((ctx.dataset as any).yAxisID === "yVol") {
                      return `${lbl}: ${fmtK(y)} vol`;
                    }
                    return `${lbl}: ${y != null ? y.toFixed(2) : "—"}`;
                  },
                },
              },
            },
            elements: { point: { radius: 0 } },
            scales: {
              x: {
                ticks: { maxRotation: 0, autoSkip: true, color: axisColor },
                grid: { color: gridColor, drawBorder: false },
              },
              // Left axis: price
              y: {
                beginAtZero: false,
                ticks: { color: axisColor },
                grid: { color: gridColor, drawBorder: false },
              },
              // Right axis: volume
              yVol: {
                position: "right",
                beginAtZero: true,
                ticks: {
                  color: axisColor,
                  maxTicksLimit: 6,
                  callback: (v) => fmtK(Number(v)),
                },
                grid: { drawOnChartArea: false, drawBorder: false },
              },
            },
          }}
        />
      </div>

      <small>RTH only (09:30–16:00 New York). Timestamps shown in NY time (EST/EDT).</small>
    </div>
  );
}
